import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { emailsAPI } from '../../tracker/api/emails.api';
import { useSocket } from '@/contexts/SocketContext';

/**
 * Hook for the navbar badge on the Emails tab.
 * Returns the sum of 'interesting' + 'new_affaire' triage counts.
 *
 * T-navbar-badges / 2026-05-18: subscribes to the
 * `email:triage:updated` socket event so the badge moves without a
 * page refresh. Backend emitter lives in
 * ClaudeAgentController.emailTriageExecute (broadcast to role:admin +
 * role:bureau after the triage UPDATE batch). When the socket is
 * disconnected the React Query refetch-on-focus + 30 s staleTime
 * fallback still keeps the count fresh.
 */
export const useEmailTriageBadgeCount = () => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  const { data } = useQuery({
    queryKey: ['triage-stats'],
    queryFn: () => emailsAPI.getTriageStats(),
    staleTime: 30000,
    gcTime: 60000,
    // Safety-net poll: the badge otherwise only moves on the role-room
    // `email:triage:updated` socket event, which is lost if the socket is
    // disconnected when triage runs — leaving the count stale until a page
    // reload. A 60s background refetch guarantees convergence; the socket
    // event still drives the instant update when connected.
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!socket) return;
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ['triage-stats'] });
    };
    socket.on('email:triage:updated', invalidate);
    // Fallback hook: if the raw email-sync ever starts emitting a
    // 'email:received' event, the badge picks up the +1 in the pending
    // bucket too — listener is harmless if the event never fires.
    socket.on('email:received', invalidate);
    return () => {
      socket.off('email:triage:updated', invalidate);
      socket.off('email:received', invalidate);
    };
  }, [socket, queryClient]);

  const stats = data?.data;
  const badgeCount = (stats?.interesting || 0) + (stats?.new_affaire || 0);

  return { badgeCount };
};
